import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * RoleBasedRoute Component
 *
 * Renders different components based on the user's role stored in localStorage.
 * This provides UI-level role checking for better UX.
 *
 * SECURITY NOTE:
 * - Frontend role checking is for UI/UX only
 * - Backend MUST validate roles on every API call
 * - Token validation happens on backend - if user tampers with localStorage,
 *   API calls will fail with 403 Forbidden
 *
 * FUTURE ENHANCEMENT:
 * - Can add backend validation by uncommenting the useEffect validation code
 * - Can add loading states during validation
 * - Can add error boundaries for better error handling
 *
 * @param {React.Component} adminComponent - Component to render for ADMIN role
 * @param {React.Component} userComponent - Component to render for CLIENT role
 * @param {boolean} adminOnly - If true, only ADMIN can access (blocks CLIENT)
 * @param {boolean} userOnly - If true, only CLIENT can access (blocks ADMIN)
 * @param {boolean} requireAuth - If true, requires authentication (default: true)
 */
const RoleBasedRoute = ({
  adminComponent,
  userComponent,
  adminOnly = false,
  userOnly = false,
  requireAuth = true,
}) => {
  // Auto-login when a token is handed off via URL (e.g. from the user-web app)
  const urlToken = new URLSearchParams(window.location.search).get('token');
  if (urlToken) {
    localStorage.setItem('token', urlToken);
    localStorage.setItem('userRole', 'ADMIN');
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  const userRole = localStorage.getItem('userRole');
  const token = localStorage.getItem('token');

  // ========================================
  // AUTHENTICATION CHECK
  // ========================================
  if (requireAuth && !token) {
    console.log('RoleBasedRoute: No token found, redirecting to login');
    return <Navigate to="/" replace />;
  }

  // ========================================
  // ROLE-BASED ACCESS CONTROL
  // ========================================

  // Admin-only route - block non-admins
  if (adminOnly && userRole !== 'ADMIN') {
    console.log(`RoleBasedRoute: Admin-only route, user role is ${userRole}, redirecting to dashboard`);
    return <Navigate to="/dashboard" replace />;
  }

  // User-only route - block non-users
  if (userOnly && userRole !== 'CLIENT') {
    console.log(`RoleBasedRoute: User-only route, user role is ${userRole}, redirecting to dashboard`);
    return <Navigate to="/dashboard" replace />;
  }

  // ========================================
  // RENDER APPROPRIATE COMPONENT
  // ========================================

  if (userRole === 'ADMIN') {
    console.log('RoleBasedRoute: Rendering ADMIN component');
    return adminComponent || <Navigate to="/" replace />;
  } else if (userRole === 'CLIENT') {
    console.log('RoleBasedRoute: CLIENT role detected, redirecting to /member/dashboard');
    // CLIENT users should use /member/* routes only
    return <Navigate to="/member/dashboard" replace />;
  }

  // Unknown or invalid role - logout and redirect
  console.error('RoleBasedRoute: Unknown role or no role found, clearing localStorage');
  localStorage.clear();
  return <Navigate to="/" replace />;
};

export default RoleBasedRoute;

/**
 * FUTURE ENHANCEMENT: Backend Validation
 *
 * Uncomment this code to add backend validation on route access:
 *
 * import { useEffect, useState } from 'react';
 * import axios from 'axios';
 *
 * const [isValidating, setIsValidating] = useState(true);
 * const [isAuthorized, setIsAuthorized] = useState(false);
 *
 * useEffect(() => {
 *   const validateAccess = async () => {
 *     if (!token) {
 *       setIsValidating(false);
 *       return;
 *     }
 *
 *     try {
 *       // Call backend to verify token and role
 *       const response = await axios.get(
 *         `${process.env.REACT_APP_API_BASE_URL}/auth/verify-role`,
 *         {
 *           headers: { Authorization: `Bearer ${token}` }
 *         }
 *       );
 *
 *       const backendRole = response.data.role;
 *
 *       // Check if localStorage matches backend
 *       if (backendRole !== userRole) {
 *         console.error('Role mismatch detected!');
 *         localStorage.clear();
 *         setIsAuthorized(false);
 *       } else {
 *         setIsAuthorized(true);
 *       }
 *     } catch (error) {
 *       console.error('Token validation failed:', error);
 *       localStorage.clear();
 *       setIsAuthorized(false);
 *     } finally {
 *       setIsValidating(false);
 *     }
 *   };
 *
 *   validateAccess();
 * }, [token, userRole]);
 *
 * if (isValidating) {
 *   return <div className="flex items-center justify-center min-h-screen">
 *     <div>Validating access...</div>
 *   </div>;
 * }
 *
 * if (!isAuthorized) {
 *   return <Navigate to="/" replace />;
 * }
 */
